'use client';

import { LocalJSONStorage } from '~/utils/js';
import { useLocalStorageState } from '~/modules/browser/storage';
import { Switch } from '~/components/ui/switch';
import { Auth } from '~/modules/auth/client';
import { createContext, use, useMemo } from 'react';

type StaffLayer_Ctx = {
    enabled: boolean;
};
const StaffLayer_Ctx = createContext<StaffLayer_Ctx>(null!);

export const StaffLayer_Ctx_Provider: React.FC<React.PropsWithChildren<StaffLayer_Ctx>> = props => (
    <StaffLayer_Ctx.Provider value={{ enabled: props.enabled }}>
        {props.children}
    </StaffLayer_Ctx.Provider>
);

// --- Staff View ---
const STAFF_VIEW_STORAGE = new LocalJSONStorage<boolean>('pdp-staff-view', true, 'localStorage');

export function useStaffLayer_State() {
    const ctx = use(StaffLayer_Ctx);

    const auth = Auth.useSession('optional');
    const store = useLocalStorageState(STAFF_VIEW_STORAGE);

    return useMemo(() => {
        const enabled =
            ctx.enabled &&
            // This ensures that the staff layer isn't shown when the user signs out
            (auth.isLoading ? true : auth.user?.isSuperAdmin);

        return {
            enabled: ctx.enabled && (auth.isLoading ? true : auth.user?.isSuperAdmin),
            shouldShow: enabled && store.state,
            setShouldShow(value: React.SetStateAction<boolean>) {
                if (!enabled) return;
                return store.setState(value);
            },
        };
    }, [ctx, auth.user?.isSuperAdmin, store.state]);
}

export const StaffLayer_Toggle: React.FC = () => {
    const state = useStaffLayer_State();

    if (!state.enabled) return null;

    const ID = 'staff-layer-toggle';

    return (
        <label htmlFor={ID} className="flex cursor-pointer items-center gap-2">
            <span className="text-sm font-medium text-blue-900">Staff Layer</span>
            <Switch
                id={ID}
                checked={state.shouldShow}
                onCheckedChange={() => state.setShouldShow(prev => !prev)}
            />
        </label>
    );
};

export const StaffLayer_Switch: React.FC<
    React.PropsWithChildren<{ fallback: React.ReactNode }>
> = props => {
    const state = useStaffLayer_State();
    return <>{state.shouldShow ? props.children : props.fallback}</>;
};
